import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import { createServerFn } from "@tanstack/react-start";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

// Public anon client for reading published rows (RLS allows it).
// Used in SSR loaders so we don't require the service-role key locally.
function publicSupabase() {
  const url = process.env.SUPABASE_URL || process.env.VITE_SUPABASE_URL;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY || process.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) {
    throw new Error("Missing SUPABASE_URL / SUPABASE_PUBLISHABLE_KEY");
  }
  return createClient<Database>(url, key, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

const MetricSchema = z.object({
  value: z.string().trim().min(1).max(40),
  label_en: z.string().trim().min(1).max(60),
  label_es: z.string().trim().min(1).max(60),
});

const EngagementType = z.enum(["", "performance", "cro-seo", "custom-build", "retainer"]).default("");

const ProjectInputSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9-]+$/, "lowercase letters, numbers, hyphens only"),
  title: z.string().trim().min(1).max(120),
  tag_en: z.string().trim().max(120).default(""),
  tag_es: z.string().trim().max(120).default(""),
  body_en: z.string().trim().max(1000).default(""),
  body_es: z.string().trim().max(1000).default(""),
  alt_en: z.string().trim().max(200).default(""),
  alt_es: z.string().trim().max(200).default(""),
  image_url: z.string().trim().max(500).nullable().optional(),
  featured: z.boolean().default(false),
  metrics: z.array(MetricSchema).max(6).default([]),
  sort_order: z.number().int().default(0),
  published: z.boolean().default(true),
  external_url: z.string().trim().max(500).nullable().optional(),
  headline_en: z.string().trim().max(200).default(""),
  headline_es: z.string().trim().max(200).default(""),
  problem_en: z.string().trim().max(500).default(""),
  problem_es: z.string().trim().max(500).default(""),
  fix_en: z.string().trim().max(500).default(""),
  fix_es: z.string().trim().max(500).default(""),
  context_en: z.string().trim().max(200).default(""),
  context_es: z.string().trim().max(200).default(""),
  engagement_type: EngagementType,
});

export type ProjectInput = z.infer<typeof ProjectInputSchema>;

// Public: list published projects (used by homepage + work pages)
export const listPublicProjects = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const { data, error } = await publicSupabase()
      .from("projects")
      .select("id, slug, title, tag_en, tag_es, body_en, body_es, alt_en, alt_es, image_url, featured, metrics, sort_order, external_url, headline_en, headline_es, problem_en, problem_es, fix_en, fix_es, context_en, context_es, engagement_type")
      .eq("published", true)
      .order("featured", { ascending: false })
      .order("sort_order", { ascending: true });
    if (error) {
      console.error("listPublicProjects error", error);
      return { projects: [] };
    }
    return { projects: data ?? [] };
  } catch (err) {
    console.error("listPublicProjects exception", err);
    return { projects: [] };
  }
});

// Admin: list all projects (drafts included)
export const listAdminProjects = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data: roleRow } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();
    if (!roleRow) throw new Error("Forbidden");

    const { data, error } = await supabaseAdmin
      .from("projects")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw new Error(error.message);
    return { projects: data ?? [] };
  });

const IdSchema = z.object({ id: z.string().uuid() });

export const getProject = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roleRow } = await supabase
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Error("Forbidden");

    const { data: row, error } = await supabaseAdmin
      .from("projects").select("*").eq("id", data.id).maybeSingle();
    if (error) throw new Error(error.message);
    return { project: row };
  });

export const upsertProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ProjectInputSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roleRow } = await supabase
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Error("Forbidden");

    const payload = {
      slug: data.slug,
      title: data.title,
      tag_en: data.tag_en,
      tag_es: data.tag_es,
      body_en: data.body_en,
      body_es: data.body_es,
      alt_en: data.alt_en,
      alt_es: data.alt_es,
      image_url: data.image_url || null,
      featured: data.featured,
      metrics: data.metrics,
      sort_order: data.sort_order,
      published: data.published,
      external_url: data.external_url || null,
      headline_en: data.headline_en,
      headline_es: data.headline_es,
      problem_en: data.problem_en,
      problem_es: data.problem_es,
      fix_en: data.fix_en,
      fix_es: data.fix_es,
      context_en: data.context_en,
      context_es: data.context_es,
      engagement_type: data.engagement_type,
    };

    if (data.id) {
      const { data: row, error } = await supabaseAdmin
        .from("projects").update(payload).eq("id", data.id).select("id").single();
      if (error) throw new Error(error.message);
      return { id: row.id as string };
    } else {
      const { data: row, error } = await supabaseAdmin
        .from("projects").insert(payload).select("id").single();
      if (error) throw new Error(error.message);
      return { id: row.id as string };
    }
  });

export const deleteProject = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => IdSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roleRow } = await supabase
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Error("Forbidden");

    const { error } = await supabaseAdmin.from("projects").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const ReorderSchema = z.object({
  items: z.array(z.object({ id: z.string().uuid(), sort_order: z.number().int() })).max(200),
});

export const reorderProjects = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => ReorderSchema.parse(input))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { data: roleRow } = await supabase
      .from("user_roles").select("role").eq("user_id", userId).eq("role", "admin").maybeSingle();
    if (!roleRow) throw new Error("Forbidden");

    for (const item of data.items) {
      const { error } = await supabaseAdmin
        .from("projects").update({ sort_order: item.sort_order }).eq("id", item.id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

// Returns whether the current authenticated user is an admin
export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId, claims } = context;
    const email = (claims as { email?: string } | null)?.email ?? null;

    // Step 1: ask via the user-scoped client (RLS applies). This is what the
    // app actually relies on at request time, so any RLS misconfig surfaces here.
    const { data: ownRow, error: ownErr } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();

    if (ownRow) return { isAdmin: true, userId, email, reason: null };

    // Step 2: cross-check via service role (bypasses RLS) so we can tell the
    // user *why* they were denied, missing role vs. RLS hiding their row.
    const { data: adminRow, error: adminErr } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .eq("role", "admin")
      .maybeSingle();

    if (adminErr) {
      return {
        isAdmin: false,
        userId,
        email,
        reason: {
          code: "lookup_failed",
          message: `Couldn't look up your role: ${adminErr.message}`,
        },
      };
    }

    if (!adminRow) {
      return {
        isAdmin: false,
        userId,
        email,
        reason: {
          code: "no_admin_role",
          message:
            "Your account is signed in but has no admin role assigned in user_roles. Ask the site owner to grant you the admin role.",
        },
      };
    }

    // Row exists in DB but RLS hid it from the user-scoped query.
    return {
      isAdmin: false,
      userId,
      email,
      reason: {
        code: "rls_blocked",
        message:
          "Your admin role exists in the database, but Row-Level Security on user_roles is preventing you from reading it. Add a policy allowing authenticated users to view their own row (auth.uid() = user_id).",
        details: ownErr?.message ?? null,
      },
    };
  });
